/**
 * Activity pricing service
 *
 * Compta (https://compta.terralao.la) is the source of truth for prices. The
 * chatbot's first-screen activity buttons show a "from $X" price that must match
 * what compta actually charges. The browser can't read the products table
 * directly (cost columns), so this calls the safe get_activity_pricing RPC,
 * which returns ONLY {source_id (= the website activities.id), from_price_usd
 * (min active price), product_count}. Mirrors trekPricing.ts.
 *
 * Graceful fallback: returns [] on any failure so the chatbot falls back to its
 * static labels.
 */

const COMPTA_API_URL =
  import.meta.env.VITE_COMPTA_API_URL || 'https://compta.terralao.la/api'

export interface ActivityPricingRow {
  source_id: string
  from_price_usd: number
  product_count: number
}

export interface VariantPricingRow {
  source_id: string
  variant: string | null
  base_price_usd: number
}

/**
 * Per-variant prices from compta (get_variant_pricing RPC). This is what makes
 * enduro variant prices (half_day/full_day/2_day/6_day) live-update from the
 * back office instead of relying on the ENDURO_TOUR_PRICES_USD hardcode.
 * Returns [] on any failure so callers fall back to the hardcoded mirror.
 */
export async function fetchVariantPricing(): Promise<VariantPricingRow[]> {
  try {
    const response = await fetch(`${COMPTA_API_URL}/rpc/get_variant_pricing`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    if (!response.ok) {
      console.error('Variant pricing fetch failed:', response.status)
      return []
    }
    const rows = (await response.json()) as VariantPricingRow[]
    return rows.map((r) => ({
      source_id: r.source_id,
      variant: r.variant,
      base_price_usd: Number(r.base_price_usd),
    }))
  } catch (err) {
    console.error('Variant pricing fetch error:', err)
    return []
  }
}

export async function fetchActivityPricing(): Promise<ActivityPricingRow[]> {
  try {
    const response = await fetch(`${COMPTA_API_URL}/rpc/get_activity_pricing`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    if (!response.ok) {
      console.error('Activity pricing fetch failed:', response.status)
      return []
    }
    const rows = (await response.json()) as ActivityPricingRow[]
    return rows.map((r) => ({
      source_id: r.source_id,
      from_price_usd: Number(r.from_price_usd),
      product_count: Number(r.product_count),
    }))
  } catch (err) {
    console.error('Activity pricing fetch error:', err)
    return []
  }
}
